import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const editSkillPag = createPaginationModule('EDIT_SKILL');

const state = {
    skill: null,
    personsWithSkill: [],
    allVolunteers: [],
    skillLevels: [],
    workgroups: [],
    interestedPersons: [],
    loading: false,
    error: null,
    pagination: defaultPagination(),
};

const mutations = {
    ...editSkillPag.mutations,

    SET_SKILL(state, skill) {
        state.skill = skill;
    },
    SET_PERSONS_WITH_SKILL(state, persons) {
        state.personsWithSkill = persons;
    },
    SET_ALL_VOLUNTEERS(state, volunteers) {
        state.allVolunteers = volunteers;
    },
    SET_SKILL_LEVELS(state, levels) {
        state.skillLevels = levels;
    },
    SET_WORKGROUPS(state, workgroups) {
        state.workgroups = workgroups;
    },
    SET_INTERESTED_PERSONS(state, persons) {
        state.interestedPersons = persons;
    },
    SET_LOADING(state, val) {
        state.loading = val;
    },
    SET_ERROR(state, err) {
        state.error = err;
    },
};

const actions = {
    /**
     * ✅ Fetch single skill details by ID
     * API: GET /api/skills/{id}
     */
    async getSkillById({ commit }, skillId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.get(`/skills/${skillId}`);
            const data = response.data?.output;

            if (!data) throw new Error("No skill data found");

            const mappedSkill = {
                id: data.skillid,
                name: data.skillname,
                description: data.description,
                locationId: data.locationid,
                isDefault: data.is_default ? "Yes" : "No",
                interestAutoAdd: data.interest_auto_add,
                publicView: data.publicview,
                preSelect: data.preselect,
                volunteerCount: data.volunteer_count,
            };

            commit("SET_SKILL", mappedSkill);
            return mappedSkill;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch skill details");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },


    /**
     * ✅ Save or update skill details
     */
    async saveSkillDetails({ commit }, payload) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageskillajax", {
                cmd: "saveskilldetail",
                skillid: payload.skillId,
                locationid: payload.locationId,
                newskillname: payload.name,
                newautoadd: payload.autoAddPerson ? 1 : 0,
                newpublicview: payload.publiclyViewable ? 1 : 0,
                newpreselect: payload.preselectRegistration ? 1 : 0,
                newskilldescription: payload.description,
            });

            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to save skill details");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Fetch persons with this skill + all volunteers
     * API: POST /manageskillajax
     * Body: { cmd: "getskillpersonsjson", skillid, locationid }
     */
    async getSkillPersons({ commit }, { skillId, locationId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageskillajax", {
                cmd: "getskillpersonsjson",
                skillid: skillId,
                locationid: locationId,
            });

            const { persons_with_skill, all_volunteers } = response.data;

            commit("SET_PERSONS_WITH_SKILL", persons_with_skill || []);
            commit("SET_ALL_VOLUNTEERS", all_volunteers || []);

            return {
                personsWithSkill: persons_with_skill,
                allVolunteers: all_volunteers,
            };
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch skill persons data");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Save persons assigned to a skill
     * API: POST /manageskillajax
     * Body: { cmd: "saveskillpersons", skillid, locationid, useridsarray: [] }
     */
    async saveSkillPersons({ commit }, { skillId, locationId, userIds }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageskillajax", {
                cmd: "saveskillpersons",
                skillid: skillId,
                locationid: locationId,
                useridsarray: userIds,
            });

            if (!response.data.success)
                throw new Error(response.data.message || "Failed to save assigned persons");

            return response.data; // { success: true, message: "...", ... }
        } catch (error) {
            const message = handleApiError(error, "Failed to save skill persons");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ NEW: Fetch skill levels for a specific skill
     * API: POST /manageskillajax
     * Body: { cmd: "getskilllevels", skillid, locationid }
     */
    async getSkillLevels({ commit }, { skillId, locationId, search = "", page = 1, pageSize = 10 }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageskillajax", {
                cmd: "getskilllevels",
                skillid: skillId,
                locationid: locationId,
                search,
                page_number: page,
                page_size: pageSize
            });

            const { data, pagination } = response.data;

            commit("SET_SKILL_LEVELS", data);

            commit(editSkillPag.SET_PAGINATION,
                {
                    currentPage: pagination.current_page,
                    totalPages: pagination.total_pages,
                    totalRecords: pagination.total_records,
                    pageSize: pagination.page_size,
                    hasPrevious: pagination.has_previous,
                    hasNext: pagination.has_next,
                });
            return data;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch skill levels");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Save or update a person's skill level
     * API: POST /manageskillajax
     * Body: { cmd: "saveskilllevel", skillid, locationid, skilllevel, personid }
     */
    async saveSkillLevel({ commit }, { skillId, locationId, skillLevel, personId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageskillajax", {
                cmd: "saveskilllevel",
                skillid: skillId,
                locationid: locationId,
                skilllevel: skillLevel,
                personid: personId,
            });

            if (!response.data.success) {
                throw new Error(response.data.message || "Failed to save skill level");
            }

            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to save skill level");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Fetch workgroups for a specific skill
     * API: POST /manageskillajax
     * Body: { cmd: "getskillworkgroups", skillid, locationid }
     */
    async getSkillWorkgroups({ commit }, { skillId, locationId, search = "", page = 1, pageSize = 10 }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageskillajax", {
                cmd: "getskillworkgroups",
                skillid: skillId,
                locationid: locationId,
                search,
                page_number: page,
                page_size: pageSize,
            });

            const { data, pagination } = response.data;
            commit("SET_WORKGROUPS", data);

            commit(editSkillPag.SET_PAGINATION, {
                currentPage: pagination.current_page,
                totalPages: pagination.total_pages,
                totalRecords: pagination.total_records,
                pageSize: pagination.page_size,
                hasPrevious: pagination.has_previous,
                hasNext: pagination.has_next,
            });

            return data;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch skill workgroups");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Fetch interested persons for a skill (with pagination)
     * API: POST /manageskillajax
     */
    async getInterestedPersons(
        { commit },
        { skillId, locationId, pageNumber = 1, pageSize = 10, search = "" }
    ) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageskillajax", {
                cmd: "getinterestedpersonsjson",
                skillid: skillId,
                locationid: locationId,
                page_number: pageNumber,
                page_size: pageSize,
                search,
            });

            const { data, pagination } = response.data || {};

            if (!data) throw new Error("No interested persons data found");

            commit("SET_INTERESTED_PERSONS", data);

            commit(editSkillPag.SET_PAGINATION, {
                currentPage: pagination.current_page,
                totalPages: pagination.total_pages,
                totalRecords: pagination.total_records,
                pageSize: pagination.page_size,
                hasPrevious: pagination.has_previous,
                hasNext: pagination.has_next,
            });

            return { data, pagination };
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch interested persons");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    skill: (state) => state.skill,
    personsWithSkill: (state) => state.personsWithSkill,
    skillLevels: (state) => state.skillLevels,
    skillLevelPagination: (state) => state.pagination,
    allVolunteers: (state) => state.allVolunteers,
    skillPersonWorkgroups: (state) => state.workgroups,
    interestedPersons: (state) => state.interestedPersons,
    skillLoading: (state) => state.loading,
    skillError: (state) => state.error,
};

export default {
    state,
    mutations,
    actions,
    getters,
};
